import { useContext, useState } from 'react';
import firebase from 'firebase/compat/app';
import auth from 'services/Auth/Firebase';
import { Button, Input, Form, message } from 'antd';
import {
  MailOutlined,
  LockOutlined,
  GoogleOutlined,
  AppleFilled,
} from '@ant-design/icons';

import MFAVerification from '../../../components/MFA/MFAVerification';
import MandatoryMFAEnrollment from '../../../components/MFA/MandatoryMFAEnrollment';

import '../Auth.sass';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../constants/routes';
import { AuthContext } from 'contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [showMandatoryMFA, setShowMandatoryMFA] = useState(() => {
    if (currentUser) {
      return currentUser.multiFactor?.enrolledFactors?.length === 0;
    }

    return false;
  });
  const [mfaResolver, setMfaResolver] =
    useState<firebase.auth.MultiFactorResolver | null>(null);
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const decideNextStep = (user: firebase.User | null) => {
    if (!user) {
      return;
    }

    if (user.multiFactor?.enrolledFactors?.length > 0) {
      setShowMFAVerification(true);
    } else {
      setShowMandatoryMFA(true);
    }
  };

  const handleEmailSubmit = async (values: { email: string }) => {
    setLoading(true);
    setEmail(values.email);
    form.setFieldsValue({ email: values.email });

    try {
      await auth.signInWithEmailAndPassword(values.email, 'dummy-password');
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        navigate(`${ROUTES.SIGN_UP}?email=${encodeURIComponent(values.email)}`);
      } else if (error.code === 'auth/wrong-password') {
        setShowPassword(true);
      } else if (error.code === 'auth/invalid-email') {
        message.error('Invalid email address');
      } else {
        message.error('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (values: { password: string }) => {
    setLoading(true);
    try {
      const userCredential = await auth.signInWithEmailAndPassword(
        email,
        values.password,
      );
      decideNextStep(userCredential?.user);
    } catch (error: any) {
      console.error('Sign in error:', error);

      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(error.resolver);
        setShowMFAVerification(true);
      } else if (error.code === 'auth/wrong-password') {
        message.error('Incorrect password');
      } else if (error.code === 'auth/user-disabled') {
        message.error('This account has been disabled');
      } else {
        message.error(error.message || 'Sign in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (values: { email: string }) => {
    setLoading(true);
    try {
      await auth.sendPasswordResetEmail(values.email);
      message.success('Password reset instructions sent to your email');
      setShowPasswordReset(false);
    } catch (error: any) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        message.error('No account found with this email address');
      } else if (error.code === 'auth/invalid-email') {
        message.error('Invalid email address');
      } else {
        message.error(error.message || 'Failed to send password reset email');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const userCredential = await auth.signInWithPopup(provider);
      decideNextStep(userCredential?.user);
    } catch (error: any) {
      console.error('Google sign in error:', error);

      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(error.resolver);
        setShowMFAVerification(true);
      } else if (error.code === 'auth/popup-closed-by-user') {
        message.warning('Sign in cancelled');
      } else {
        message.error(error.message || 'Google sign in failed');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    try {
      const provider = new firebase.auth.OAuthProvider('apple.com');
      provider.addScope('name');
      provider.addScope('email');

      const userCredential = await auth.signInWithPopup(provider);
      decideNextStep(userCredential?.user);
    } catch (error: any) {
      console.error('Apple sign in error:', error);

      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(error.resolver);
        setShowMFAVerification(true);
      } else if (error.code === 'auth/popup-closed-by-user') {
        message.warning('Sign in cancelled');
      } else {
        message.error(error.message || 'Apple sign in failed');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleMFASuccess = () => {
    setShowMFAVerification(false);
    setShowMandatoryMFA(false);
    setMfaResolver(null);

    navigate(ROUTES.HOME);
  };

  const handleMandatoryMFASuccess = () => {
    setShowMandatoryMFA(false);
    navigate(ROUTES.HOME);
  };

  const handleBack = () => {
    setShowPassword(false);
    setShowPasswordReset(false);
    setEmail('');
    form.resetFields();
    form.setFieldsValue({
      email: '',
      password: '',
    });
  };

  const handleRequiresRecentLogin = () => {
    message.error('For your security, please sign in again to continue');
    setShowMandatoryMFA(false);
    setShowMFAVerification(false);
    setMfaResolver(null);
    setEmail('');
    form.resetFields();
    form.setFieldsValue({
      email: '',
      password: '',
    });
    auth.signOut();
    navigate(ROUTES.SIGN_IN);
  };

  const handleTroubleSigningIn = () => {
    setShowPasswordReset(true);
    if (email) {
      form.setFieldsValue({ email });
    }
  };

  if (showMandatoryMFA) {
    return (
      <MandatoryMFAEnrollment
        onSuccess={handleMandatoryMFASuccess}
        onRequiresRecentLogin={handleRequiresRecentLogin}
      />
    );
  }

  if (showMFAVerification) {
    return (
      <MFAVerification
        resolver={mfaResolver}
        onSuccess={handleMFASuccess}
        onRequiresRecentLogin={handleRequiresRecentLogin}
      />
    );
  }

  return (
    <div className="auth-wrapper">
      <img
        className="auth-wrapper__logo"
        src="/images/Usefull_logo.png"
        alt="Usefull logo"
      />

      <div style={{ maxWidth: 400, margin: '0 auto', padding: '20px' }}>
        {!showPassword && !showPasswordReset ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
                Sign in
              </p>
            </div>

            <Button
              icon={<GoogleOutlined />}
              onClick={handleGoogleSignIn}
              loading={googleLoading}
              block
              size="large"
              style={{
                justifyContent: 'flex-start',
              }}
            >
              Continue with Google
            </Button>

            <Button
              icon={<AppleFilled />}
              onClick={handleAppleSignIn}
              loading={appleLoading}
              block
              size="large"
              style={{
                justifyContent: 'flex-start',
                backgroundColor: '#000',
                color: '#fff',
              }}
            >
              Continue with Apple
            </Button>

            <Button
              icon={<MailOutlined />}
              onClick={() => {
                setEmailLoading(true);
                setShowPassword(true);
                setEmailLoading(false);
              }}
              loading={emailLoading}
              block
              size="large"
              style={{
                justifyContent: 'flex-start',
                backgroundColor: '#db4437',
                color: '#fff',
              }}
            >
              Sign in with email
            </Button>
          </div>
        ) : showPasswordReset ? (
          <div>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
                Recover password
              </p>
            </div>

            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
                Get instructions sent to this email that explain how to reset
                your password
              </p>
            </div>

            <Form
              form={form}
              onFinish={handlePasswordReset}
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter your email"
                  size="large"
                  autoComplete="email"
                />
              </Form.Item>

              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <Button
                  onClick={handleBack}
                  block
                  size="large"
                  style={{ flex: 1 }}
                >
                  CANCEL
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  style={{ flex: 1 }}
                >
                  SEND
                </Button>
              </div>
            </Form>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
                Sign in
              </p>
            </div>

            <Form
              form={form}
              onFinish={(values: any) => {
                if (!email) {
                  handleEmailSubmit(values);
                } else {
                  handleSignIn(values);
                }
              }}
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Enter your email"
                  size="large"
                  disabled={!!email}
                  autoComplete="email"
                />
              </Form.Item>

              {email && (
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: 'Please enter your password' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter your password"
                    size="large"
                    autoComplete="current-password"
                  />
                </Form.Item>
              )}

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                >
                  {email ? 'SIGN IN' : 'CONTINUE'}
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}

        {email && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="link" size="small" onClick={handleTroubleSigningIn}>
              Trouble signing in?
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
